var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isEqual, isNull, orderBy, throttle } from 'lodash';
import cx from 'classnames';
import useExpandRow from 'components/common/use-expand-row/useExpandRow';
import useHasScroll from 'components/common/use-has-scroll/useHasScroll';
import { SORT_DIRECTION, UI_EVENTS } from 'constants/app.constant';
import { scrollToTable as scrollToTableService } from 'services/table.service';
import ExpandAllButton from '../expand-all-button/ExpandAllButton';
import useExpandAllRow from '../use-expand-all-row/useExpandAllRow';
import { getFirstColumnMinWidth, getRowMinWidth } from './ResponsiveTableHelper';
import ResponsiveTableHeader from './ResponsiveTableHeader';
import ResponsiveTableBody from './ResponsiveTableBody';
export * from './ResponsiveTable.types';
export { renderBodyRow, wrapRowInTable } from './BodyRow';
var FIRST_COLUMN_INDEX = 0;
var withGenericMemo = React.memo;
var arePropsEqual = function (prevProps, nextProps) {
    var result = true;
    if (prevProps.rerenderFlag) {
        result = isEqual(prevProps.rerenderFlag, nextProps.rerenderFlag);
    }
    if (result) {
        result = isEqual(prevProps.records, nextProps.records);
    }
    return result;
};
export var sortRecords = function (columns, records, field, direction) {
    var sortColumn = columns.find(function (column) { return column.field === field; });
    if (!sortColumn) {
        return records;
    }
    var nullItemRecords = sortColumn.sortNARecordInBottom ? records.filter(function (record) { return isNull(record[field]); }) : [];
    var notNullItemRecords = sortColumn.sortNARecordInBottom ? records.filter(function (record) { return !isNull(record[field]); }) : records;
    var sortedRecords;
    if (sortColumn && sortColumn.customSort) {
        var revertValue_1 = direction === SORT_DIRECTION.DESC ? -1 : 1;
        sortedRecords = __spreadArray([], notNullItemRecords, true).sort(function (r1, r2) { return revertValue_1 * sortColumn.customSort(r1, r2); });
    }
    else {
        sortedRecords = orderBy(__spreadArray([], notNullItemRecords, true), field, direction);
    }
    return __spreadArray(__spreadArray([], sortedRecords, true), nullItemRecords, true);
};
var BREAD_CRUMB_HEIGHT = 78;
var ResponsiveTable = function (_a) {
    var columns = _a.columns, records = _a.records, tableProps = _a.tableProps;
    var visibleColumns = useMemo(function () { return columns.filter(function (c) { return !c.hidden; }); }, [columns]);
    var rowMinWidth = getRowMinWidth(columns);
    var firstColumnMinWidth = getFirstColumnMinWidth(visibleColumns[FIRST_COLUMN_INDEX]);
    var tableRef = useRef(null);
    var headerScrollRef = useRef(null);
    var bodyRef = useRef(null);
    var _b = useHasScroll(), hasScrollLeft = _b.hasScrollLeft, hasScrollRight = _b.hasScrollRight, setHasScroll = _b.setHasScroll;
    var _c = useState(0), bodyWidth = _c[0], setBodyWidth = _c[1];
    var isTableExpandedAllEnabled = tableProps.isTableExpandedAllEnabled, recordChildrenField = tableProps.recordChildrenField, customHeaders = tableProps.customHeaders, isSlideSwitch = tableProps.isSlideSwitch, customRenderExpandAllButton = tableProps.customRenderExpandAllButton;
    var _d = useExpandRow({ trackAAFunction: tableProps === null || tableProps === void 0 ? void 0 : tableProps.trackAAFunction }), handleExpandRow = _d.handleExpandRow, isRowExpanded = _d.isRowExpanded;
    var _e = useExpandAllRow(records, tableProps), expandState = _e.expandState, expandedRowState = _e.rowState, checkExpandStateById = _e.checkExpandStateById, changeExpandStateById = _e.changeExpandStateById, handleCollapseAllRows = _e.handleCollapseAllRows, handleExpandAllRows = _e.handleExpandAllRows;
    var _f = useState(tableProps.defaultSortField), sortField = _f[0], setSortField = _f[1];
    var _g = useState(tableProps.defaultSortDirection || SORT_DIRECTION.ASC), sortDirection = _g[0], setSortDirection = _g[1];
    var _h = useState([]), displayedRecords = _h[0], setDisplayRecords = _h[1];
    var firstColActualWidth = Math.round((Math.max(bodyWidth || rowMinWidth, rowMinWidth) * firstColumnMinWidth) / rowMinWidth);
    var scrollToTable = useCallback(function () {
        if (tableRef && tableRef.current) {
            scrollToTableService(tableRef.current.offsetTop, tableRef.current.getBoundingClientRect().top < BREAD_CRUMB_HEIGHT ? 'auto' : 'smooth');
        }
    }, []);
    var onHeaderClick = function (field) {
        var _a = tableProps.scrollToTopAfterSort, scrollToTopAfterSort = _a === void 0 ? true : _a, getCurrentSortStatus = tableProps.getCurrentSortStatus;
        var newDirection;
        if (field === sortField) {
            newDirection = sortDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;
        }
        else {
            newDirection = SORT_DIRECTION.ASC;
        }
        setSortDirection(newDirection);
        setSortField(field);
        if (getCurrentSortStatus) {
            getCurrentSortStatus(field, newDirection);
        }
        if (scrollToTopAfterSort) {
            scrollToTable();
        }
    };
    useEffect(function () {
        setDisplayRecords(sortRecords(columns, records, sortField, sortDirection));
    }, [columns, records, sortDirection, sortField]);
    useEffect(function () {
        // Both containers have the same max scroll range:
        //   header-inner: (rowMinWidth - firstColWidth) - (containerWidth - firstColWidth) = rowMinWidth - containerWidth
        //   body-outer:    rowMinWidth - containerWidth
        // So direct scrollLeft assignment is always exact.
        // The check-before-set guard breaks the feedback loop:
        // after A sets B, B's scroll event fires but sees A === B and skips, ending the cycle.
        var onHeaderScroll = function () {
            var h = headerScrollRef.current;
            var b = bodyRef.current;
            if (!h || !b)
                return;
            if (b.scrollLeft !== h.scrollLeft) {
                b.scrollLeft = h.scrollLeft;
            }
            setHasScroll(h);
        };
        var onBodyScroll = function () {
            var h = headerScrollRef.current;
            var b = bodyRef.current;
            if (!h || !b)
                return;
            if (h.scrollLeft !== b.scrollLeft) {
                h.scrollLeft = b.scrollLeft;
            }
            setHasScroll(b);
        };
        var onDocumentResize = throttle(function () {
            setHasScroll(headerScrollRef.current);
            if (bodyRef.current) {
                setBodyWidth(bodyRef.current.clientWidth);
            }
        }, 150);
        var headerScrollEl = headerScrollRef.current;
        var bodyScrollEl = bodyRef.current;
        if (headerScrollEl) {
            headerScrollEl.addEventListener('scroll', onHeaderScroll);
            setHasScroll(headerScrollEl);
        }
        if (bodyScrollEl) {
            bodyScrollEl.addEventListener('scroll', onBodyScroll);
        }
        var scrollEventType = UI_EVENTS.scrollToTable;
        if (tableProps.tableId) {
            scrollEventType += tableProps.tableId;
        }
        window.addEventListener('resize', onDocumentResize);
        window.addEventListener(scrollEventType, scrollToTable);
        return function () {
            window.removeEventListener('resize', onDocumentResize);
            window.removeEventListener(scrollEventType, scrollToTable);
            if (headerScrollEl)
                headerScrollEl.removeEventListener('scroll', onHeaderScroll);
            if (bodyScrollEl)
                bodyScrollEl.removeEventListener('scroll', onBodyScroll);
        };
    }, [scrollToTable, setHasScroll, tableProps.tableId]);
    useLayoutEffect(function () {
        if (bodyRef.current) {
            setBodyWidth(bodyRef.current.clientWidth);
        }
    }, []);
    var expandAllButtonProps = {
        isExpandAll: expandState,
        handleExpandAll: expandState ? handleCollapseAllRows : handleExpandAllRows,
        hideCondition: displayedRecords.every(function (record) { return !record[recordChildrenField]; }) || !isTableExpandedAllEnabled,
        isSlideSwitch: isSlideSwitch,
        customRenderExpandAllButton: customRenderExpandAllButton
    };
    return (<>
      <ExpandAllButton {...expandAllButtonProps}/>
      <div ref={tableRef} role="region" aria-label={tableProps.tableLabel || 'Data table'} className={cx('c-responsive-table', { 'c-responsive-table--right-inner-shadow': hasScrollRight }, tableProps.cssModifier)}>
        <ResponsiveTableHeader visibleColumns={visibleColumns} firstColActualWidth={firstColActualWidth} firstColumnMinWidth={firstColumnMinWidth} rowMinWidth={rowMinWidth} headerScrollRef={headerScrollRef} hasScrollLeft={hasScrollLeft} customHeaders={customHeaders} tableProps={tableProps} sortField={sortField} sortDirection={sortDirection} onHeaderClick={onHeaderClick}/>
        <ResponsiveTableBody bodyRef={bodyRef} bodyWidth={bodyWidth} rowMinWidth={rowMinWidth} visibleColumns={visibleColumns} displayedRecords={displayedRecords} tableProps={tableProps} hasScrollLeft={hasScrollLeft} handleExpandRow={handleExpandRow} isRowExpanded={isRowExpanded} checkExpandStateById={checkExpandStateById} changeExpandStateById={changeExpandStateById} expandedRowState={expandedRowState}/>
      </div>
    </>);
};
export default withGenericMemo(ResponsiveTable, arePropsEqual);
